import React, { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ImportWithMappingDialog from '../../../components/common/ImportWithMappingDialog';
import employeeService from '../../../services/employeeService';
import { companyService } from '../../../services/companyService';
import type { ImportServiceAdapter, MappingColumnConfig } from '../../../types/import';

interface EmployeeImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

const COLUMNS: MappingColumnConfig[] = [
  { key: 'cargo_nome', label: 'Cargo', valueType: 'number' },
  { key: 'genero', label: 'Gênero', valueType: 'string' },
  { key: 'estado_civil', label: 'Estado Civil', valueType: 'string' },
];

const COLUMN_LABELS: Record<string, string> = {
  cargo_nome: 'Cargo',
  genero: 'Gênero',
  estado_civil: 'Estado Civil',
};

const employeeImportAdapter: ImportServiceAdapter = {
  previewImport: (file, options) => employeeService.previewImport(file, options as { companyId?: number }),
  executeImport: async (file, mappings, options) => {
    const result = await employeeService.importEmployees(file, {
      ...(options as { companyId?: number }),
      mappings: mappings as Record<string, Record<string, string | number>> | undefined,
    });
    return {
      imported: result.success,
      errors: result.errors.map(e => ({ row: e.line, error: e.error })),
      warnings: result.warnings?.map(w => ({ row: w.line, warning: w.warning })),
    };
  },
  downloadTemplate: () => employeeService.downloadTemplate(),
};

const EmployeeImportDialog: React.FC<EmployeeImportDialogProps> = ({
  open,
  onClose,
  onImportComplete,
}) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('');
  const [companies, setCompanies] = useState<Array<{ id: number; name: string }>>([]);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) {
      companyService.getCompaniesForDropdown().then(setCompanies).catch(() => {});
    }
  }, [open]);

  const getImportOptions = useCallback(() => {
    if (selectedCompanyId) return { companyId: Number(selectedCompanyId) };
    return {};
  }, [selectedCompanyId]);

  const handleImportComplete = useCallback(() => {
    enqueueSnackbar('Funcionário(s) importado(s) com sucesso!', { variant: 'success' });
    onImportComplete();
  }, [enqueueSnackbar, onImportComplete]);

  const handleClose = useCallback(() => {
    setSelectedCompanyId('');
    onClose();
  }, [onClose]);

  return (
    <ImportWithMappingDialog
      open={open}
      onClose={handleClose}
      onImportComplete={handleImportComplete}
      title="Importar Funcionários"
      description="Selecione um arquivo Excel (.xlsx) com os funcionários para importar. CPFs duplicados serão ignorados."
      entityLabel="funcionário"
      templateFilename="template-importacao-funcionarios.xlsx"
      adapter={employeeImportAdapter}
      columns={COLUMNS}
      columnLabels={COLUMN_LABELS}
      getImportOptions={getImportOptions}
      scopeSelector={
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Empresa para importação:</Typography>
          {companies.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Empresa</InputLabel>
              <Select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value as number)}
                label="Empresa"
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      }
    />
  );
};

export default EmployeeImportDialog;
